import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useHistory } from 'react-router-dom';
import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import http from '../../services/http';

const safeLower = (v) => String(v || '').toLowerCase();

const companyLabel = (c) => c?.business_name || c?.name || 'Empresa sem nome';
const companyMeta = (c) => {
  const parts = [];
  if (c?.code) parts.push(`Código: ${c.code}`);
  if (c?.cnpj) parts.push(`CNPJ: ${c.cnpj}`);
  return parts.join(' • ');
};

const ClientSearchPage = () => {
  const { theme } = useContext(ThemeContext);
  const history = useHistory();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const [companyQuery, setCompanyQuery] = useState('');
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [companiesError, setCompaniesError] = useState(null);
  const [companies, setCompanies] = useState([]);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [clientsByCompanyId, setClientsByCompanyId] = useState({});
  const [clientsLoadingCompanyId, setClientsLoadingCompanyId] = useState(null);
  const [clientsError, setClientsError] = useState(null);
  const [clientQuery, setClientQuery] = useState('');

  const loadCompanies = useCallback(async () => {
    setCompaniesLoading(true);
    setCompaniesError(null);
    try {
      // Endpoint já usado no sistema. Sem query → lista geral (otimização: clientes são carregados sob demanda).
      const res = await http.get('/companies?status=1');
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setCompanies(Array.isArray(list) ? list : []);
    } catch (e) {
      setCompaniesError('Não foi possível carregar as empresas.');
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const filteredCompanies = useMemo(() => {
    const q = safeLower(companyQuery).trim();
    if (!q) return companies;
    return companies.filter((c) => {
      const hay = `${companyLabel(c)} ${c?.code || ''} ${c?.cnpj || ''}`;
      return safeLower(hay).includes(q);
    });
  }, [companies, companyQuery]);

  const selectedCompanyId = selectedCompany?.id != null ? Number(selectedCompany.id) : null;

  const selectedCompanyClients = useMemo(() => {
    if (selectedCompanyId == null || Number.isNaN(selectedCompanyId)) return [];
    const list = clientsByCompanyId[selectedCompanyId];
    return Array.isArray(list) ? list : [];
  }, [clientsByCompanyId, selectedCompanyId]);

  const filteredClients = useMemo(() => {
    const q = safeLower(clientQuery).trim();
    if (!q) return selectedCompanyClients;
    return selectedCompanyClients.filter((c) => {
      const hay = `${c?.name || ''} ${c?.email || ''} ${c?.whatsapp_user_number || ''}`;
      return safeLower(hay).includes(q);
    });
  }, [selectedCompanyClients, clientQuery]);

  const ensureCompanyClientsLoaded = useCallback(async (companyId) => {
    if (!companyId) return;
    if (clientsByCompanyId[companyId]) return;
    setClientsLoadingCompanyId(companyId);
    setClientsError(null);
    try {
      const res = await http.get(`/client-search/company/${companyId}`);
      const payload = res.data;
      const clients = payload?.success ? (payload.data?.clients || []) : (payload?.data?.clients || []);
      setClientsByCompanyId((prev) => ({ ...prev, [companyId]: Array.isArray(clients) ? clients : [] }));
    } catch (e) {
      setClientsError('Não foi possível carregar os clientes desta empresa.');
      setClientsByCompanyId((prev) => ({ ...prev, [companyId]: [] }));
    } finally {
      setClientsLoadingCompanyId(null);
    }
  }, [clientsByCompanyId]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => history.goBack()}
            size="small"
            variant="outlined"
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            Voltar
          </Button>
          <SearchOutlinedIcon sx={{ color: 'primary.main' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap>
              Empresas e clientes
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Selecione uma empresa e abra os tickets do cliente
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, p: 2, overflow: 'hidden' }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              display: 'grid',
              gridTemplateColumns: isMdUp ? '420px 1fr' : '1fr',
              gap: 2,
              minHeight: 0,
            }}
          >
            <Box sx={{ minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Empresas
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    size="small"
                    label={`${filteredCompanies.length} ${filteredCompanies.length === 1 ? 'empresa' : 'empresas'}`}
                    variant="outlined"
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={loadCompanies}
                    sx={{ textTransform: 'none' }}
                  >
                    Atualizar
                  </Button>
                </Box>
              </Box>

              <TextField
                value={companyQuery}
                onChange={(e) => setCompanyQuery(e.target.value)}
                placeholder="Buscar por nome, código ou CNPJ..."
                size="small"
                sx={{ mt: 1.25 }}
              />

              <Divider sx={{ my: 1.5 }} />

              {companiesError && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {companiesError}
                </Alert>
              )}

              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 0.5 }}>
                {companiesLoading ? (
                  <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={22} />
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {filteredCompanies.map((c) => {
                      const isSelected = selectedCompany?.id === c?.id;
                      return (
                        <ListItem
                          key={`company-${c?.id}`}
                          button
                          onClick={async () => {
                            setSelectedCompany(c);
                            setClientQuery('');
                            if (c?.id) await ensureCompanyClientsLoaded(Number(c.id));
                          }}
                          sx={{
                            mb: 0.75,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            bgcolor: isSelected ? 'rgba(99, 165, 199, 0.10)' : 'transparent',
                            '&:hover': { bgcolor: isSelected ? 'rgba(99, 165, 199, 0.14)' : 'action.hover' },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                              <BusinessIcon fontSize="small" />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                                {companyLabel(c)}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {companyMeta(c) || '—'}
                              </Typography>
                            }
                          />
                        </ListItem>
                      );
                    })}

                    {!companiesLoading && filteredCompanies.length === 0 && (
                      <Box sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhuma empresa encontrada.
                        </Typography>
                      </Box>
                    )}
                  </List>
                )}
              </Box>
            </Box>

            <Box sx={{ minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Clientes
              </Typography>

              {!selectedCompany ? (
                <Box sx={{ mt: 1.25, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Selecione uma empresa para listar os clientes.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }}>
                    {companyLabel(selectedCompany)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.25 }}>
                    {companyMeta(selectedCompany) || '—'}
                  </Typography>

                  <TextField
                    value={clientQuery}
                    onChange={(e) => setClientQuery(e.target.value)}
                    placeholder="Buscar cliente por nome, e-mail ou WhatsApp..."
                    size="small"
                  />

                  {clientsError && (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                      {clientsError}
                    </Alert>
                  )}

                  <Box sx={{ mt: 1.5, flex: 1, minHeight: 0, overflow: 'auto', pr: 0.5 }}>
                    {clientsLoadingCompanyId === selectedCompanyId ? (
                      <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={22} />
                      </Box>
                    ) : (
                      <List dense disablePadding>
                        {filteredClients.map((client) => {
                          const idNum = client?.id != null ? Number(client.id) : NaN;
                          const canGo = !Number.isNaN(idNum) && idNum > 0;
                          return (
                            <ListItem
                              key={`client-${client?.id}`}
                              sx={{
                                mb: 0.75,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                              secondaryAction={
                                <IconButton
                                  edge="end"
                                  disabled={!canGo}
                                  onClick={() => history.push(`/cliente/${idNum}/tickets`)}
                                  size="small"
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              }
                            >
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'action.selected', width: 32, height: 32, color: 'text.primary' }}>
                                  <PersonIcon fontSize="small" />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                                    {client?.name || '—'}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="caption" color="text.secondary">
                                    {[client?.email, client?.whatsapp_user_number].filter(Boolean).join(' • ') || '—'}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          );
                        })}

                        {clientsLoadingCompanyId !== selectedCompanyId && filteredClients.length === 0 && (
                          <Box sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              Nenhum cliente encontrado para esta empresa.
                            </Typography>
                          </Box>
                        )}
                      </List>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ClientSearchPage;

